export interface ProspectRow {
  id: string
  business_name: string
  contact_name: string | null
  contact_role: string | null
  phone: string | null
  whatsapp: string | null
  email: string | null
  address: string | null
  province: string | null
  city: string | null
  niche_id: string | null
  source_id: string | null
  assigned_to: string | null
  instagram_url: string | null
  facebook_url: string | null
  website_url: string | null
  google_maps_url: string | null
  interest_level: string | null
  estimated_value: number | null
  currency: string | null
  product_interest: string | null
  research_notes: string | null
  notes: string | null
  status: string | null
  created_by: string | null
  created_at: string | null
}

export interface ProspectFormValues {
  business_name: string
  contact_name: string
  contact_role: string
  phone: string
  whatsapp: string
  email: string
  address: string
  province: string
  city: string
  niche_id: string
  source_id: string
  interest_level: string
  estimated_value: string
  currency: string
  product_interest: string
  website_url: string
  instagram_url: string
  facebook_url: string
  google_maps_url: string
  research_notes: string
  notes: string
}

export interface ProspectInsert {
  business_name: string
  contact_name: string | null
  contact_role: string | null
  phone: string | null
  whatsapp: string | null
  email: string | null
  address: string | null
  province: string | null
  city: string | null
  niche_id: string | null
  source_id: string | null
  assigned_to: string
  instagram_url: string | null
  facebook_url: string | null
  website_url: string | null
  google_maps_url: string | null
  interest_level: string | null
  estimated_value: number | null
  currency: string
  product_interest: string | null
  research_notes: string | null
  notes: string | null
  status: 'new'
  created_by: string
}

export interface ProspectUpdate {
  business_name: string
  contact_name: string | null
  contact_role: string | null
  phone: string | null
  whatsapp: string | null
  email: string | null
  address: string | null
  province: string | null
  city: string | null
  niche_id: string | null
  source_id: string | null
  instagram_url: string | null
  facebook_url: string | null
  website_url: string | null
  google_maps_url: string | null
  interest_level: string | null
  estimated_value: number | null
  currency: string | null
  product_interest: string | null
  research_notes: string | null
  notes: string | null
}

export const ALLOWED_PROSPECT_COLUMNS = new Set<string>([
  'business_name',
  'contact_name',
  'contact_role',
  'phone',
  'whatsapp',
  'email',
  'address',
  'province',
  'city',
  'niche_id',
  'source_id',
  'assigned_to',
  'instagram_url',
  'facebook_url',
  'website_url',
  'google_maps_url',
  'interest_level',
  'estimated_value',
  'currency',
  'product_interest',
  'research_notes',
  'notes',
  'status',
  'created_by',
])

export class UnknownProspectColumnsError extends Error {
  constructor(public readonly unknownKeys: string[]) {
    super(`Unknown prospect columns: ${unknownKeys.join(', ')}`)
    this.name = 'UnknownProspectColumnsError'
  }
}

export function createEmptyProspectForm(): ProspectFormValues {
  return {
    business_name: '',
    contact_name: '',
    contact_role: '',
    phone: '',
    whatsapp: '',
    email: '',
    address: '',
    province: '',
    city: '',
    niche_id: '',
    source_id: '',
    interest_level: '',
    estimated_value: '',
    currency: 'CRC',
    product_interest: '',
    website_url: '',
    instagram_url: '',
    facebook_url: '',
    google_maps_url: '',
    research_notes: '',
    notes: '',
  }
}

export function buildProspectPayload(
  formValues: ProspectFormValues,
  authenticatedUserId: string,
  mode: 'insert' | 'update' = 'insert',
): ProspectInsert | ProspectUpdate {
  const businessName = normalizeText(formValues.business_name)
  if (!businessName) {
    throw new Error('Ingresá el nombre del negocio para guardar el prospecto.')
  }

  const editableFields: ProspectUpdate = {
    business_name: businessName,
    contact_name: normalizeText(formValues.contact_name),
    contact_role: normalizeText(formValues.contact_role),
    phone: normalizeText(formValues.phone),
    whatsapp: normalizeText(formValues.whatsapp),
    email: normalizeText(formValues.email),
    address: normalizeText(formValues.address),
    province: normalizeText(formValues.province),
    city: normalizeText(formValues.city),
    niche_id: normalizeUuid(formValues.niche_id),
    source_id: normalizeUuid(formValues.source_id),
    instagram_url: normalizeText(formValues.instagram_url),
    facebook_url: normalizeText(formValues.facebook_url),
    website_url: normalizeText(formValues.website_url),
    google_maps_url: normalizeText(formValues.google_maps_url),
    interest_level: normalizeText(formValues.interest_level),
    estimated_value: normalizeNumber(formValues.estimated_value),
    currency: normalizeText(formValues.currency),
    product_interest: normalizeText(formValues.product_interest),
    research_notes: normalizeText(formValues.research_notes),
    notes: normalizeText(formValues.notes),
  }

  if (mode === 'update') {
    assertValidProspectPayload(editableFields)
    return editableFields
  }

  const payload: ProspectInsert = {
    business_name: editableFields.business_name,
    contact_name: editableFields.contact_name,
    contact_role: editableFields.contact_role,
    phone: editableFields.phone,
    whatsapp: editableFields.whatsapp,
    email: editableFields.email,
    address: editableFields.address,
    province: editableFields.province,
    city: editableFields.city,
    niche_id: editableFields.niche_id,
    source_id: editableFields.source_id,
    assigned_to: requireUuid(authenticatedUserId, 'assigned_to'),
    instagram_url: editableFields.instagram_url,
    facebook_url: editableFields.facebook_url,
    website_url: editableFields.website_url,
    google_maps_url: editableFields.google_maps_url,
    interest_level: editableFields.interest_level,
    estimated_value: editableFields.estimated_value,
    currency: editableFields.currency ?? 'CRC',
    product_interest: editableFields.product_interest,
    research_notes: editableFields.research_notes,
    notes: editableFields.notes,
    status: 'new',
    created_by: requireUuid(authenticatedUserId, 'created_by'),
  }
  assertValidProspectPayload(payload)
  return payload
}

export function assertValidProspectPayload(payload: object): void {
  const unknownKeys = Object.keys(payload).filter(
    (key) => !ALLOWED_PROSPECT_COLUMNS.has(key),
  )
  if (unknownKeys.length) {
    console.error('Unknown prospect columns', unknownKeys)
    throw new UnknownProspectColumnsError(unknownKeys)
  }
}

export function prospectFormFromRow(
  record: Record<string, unknown>,
): ProspectFormValues {
  return {
    business_name: readText(record.business_name),
    contact_name: readText(record.contact_name),
    contact_role: readText(record.contact_role),
    phone: readText(record.phone),
    whatsapp: readText(record.whatsapp),
    email: readText(record.email),
    address: readText(record.address),
    province: readText(record.province),
    city: readText(record.city),
    niche_id: readText(record.niche_id),
    source_id: readText(record.source_id),
    interest_level: readText(record.interest_level),
    estimated_value: readText(record.estimated_value),
    currency: readText(record.currency) || 'CRC',
    product_interest: readText(record.product_interest),
    website_url: readText(record.website_url),
    instagram_url: readText(record.instagram_url),
    facebook_url: readText(record.facebook_url),
    google_maps_url: readText(record.google_maps_url),
    research_notes: readText(record.research_notes),
    notes: readText(record.notes),
  }
}

export function prospectRowFromRecord(
  record: Record<string, unknown>,
): ProspectRow {
  return {
    id: readText(record.id),
    business_name: readText(record.business_name),
    contact_name: readNullableText(record.contact_name),
    contact_role: readNullableText(record.contact_role),
    phone: readNullableText(record.phone),
    whatsapp: readNullableText(record.whatsapp),
    email: readNullableText(record.email),
    address: readNullableText(record.address),
    province: readNullableText(record.province),
    city: readNullableText(record.city),
    niche_id: readNullableText(record.niche_id),
    source_id: readNullableText(record.source_id),
    assigned_to: readNullableText(record.assigned_to),
    instagram_url: readNullableText(record.instagram_url),
    facebook_url: readNullableText(record.facebook_url),
    website_url: readNullableText(record.website_url),
    google_maps_url: readNullableText(record.google_maps_url),
    interest_level: readNullableText(record.interest_level),
    estimated_value: readNumber(record.estimated_value),
    currency: readNullableText(record.currency),
    product_interest: readNullableText(record.product_interest),
    research_notes: readNullableText(record.research_notes),
    notes: readNullableText(record.notes),
    status: readNullableText(record.status),
    created_by: readNullableText(record.created_by),
    created_at: readNullableText(record.created_at),
  }
}

export function formatProspectSupabaseError(
  error: {
    code?: string
    message?: string
    details?: string
    hint?: string
  } | null,
): string {
  const diagnostic = [
    error?.code && `Código: ${error.code}`,
    error?.message && `Mensaje: ${error.message}`,
    error?.details && `Detalle: ${error.details}`,
    error?.hint && `Sugerencia: ${error.hint}`,
  ]
    .filter(Boolean)
    .join(' · ')

  if (error?.code === 'PGRST204') {
    return diagnostic
      ? `Existe una inconsistencia entre el formulario y la base de datos. ${diagnostic}`
      : 'Existe una inconsistencia entre el formulario y la base de datos.'
  }

  return diagnostic
    ? `No fue posible guardar el prospecto. ${diagnostic}`
    : 'No fue posible guardar el prospecto. Revisá tu conexión e intentá nuevamente.'
}

function normalizeText(value: string): string | null {
  return value.trim() || null
}

function normalizeUuid(value: string): string | null {
  const normalized = value.trim()
  return isUuid(normalized) ? normalized : null
}

function normalizeNumber(value: string): number | null {
  const normalized = value.trim().replace(',', '.')
  if (!normalized) return null
  const result = Number(normalized)
  return Number.isFinite(result) ? result : null
}

function requireUuid(value: string, field: string): string {
  if (!isUuid(value)) {
    throw new Error(`El UUID autenticado para ${field} no es válido.`)
  }
  return value
}

function isUuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    value,
  )
}

function readText(value: unknown): string {
  return value === null || value === undefined ? '' : String(value)
}

function readNullableText(value: unknown): string | null {
  const normalized = readText(value).trim()
  return normalized || null
}

function readNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}
